// 치과 「보낸 소식」 — Cloud Functions 호출 모음 (2026-10-06)
// 서버 계약: docs/계약_치과_보낸소식_웹_2026-10-06.md (서버 functions/lib/clinic-notices.js — 라벨·문구 전부 서버 1곳).
// 화면은 응답의 texts·labels·행의 label·eventLabel·resultLabel·stageLabel 을 그대로 표시한다 — 웹 사전(KIND_LABELS 등) 없음.
// 조회 전용: 재발송·수동 발송·문구 편집·내보내기 CF 는 없다(계약 머리 금지 목록). pushLogs 직접 읽기 금지(계약 §4 — rules 치과 read false).
// 오류: 기존 clinicPatientsApi.describeError 그대로(계약 §0 — failed-precondition = 환자 관리 OFF 서버 문구).
import { getFunctions, httpsCallable } from 'firebase/functions';
import { describeError } from './clinicPatientsApi';

const REGION = 'asia-northeast3';
const call = (name) => httpsCallable(getFunctions(undefined, REGION), name);

export const NOTICES_GENERIC_ERROR = '보낸 소식을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.';

// 배포일 상수 — texts.emptyLast7Since 의 {date} 치환(계약 §0). ☆ 실제 hosting 배포일에 맞춰 메인이 확정
export const NOTICES_SINCE_DATE = '2026.10.06';

// 클라이언트 접기 단위(계약 §1 — 첫 호출 limit 50, 20행씩 펼침)
export const TIMELINE_FIRST_LIMIT = 50;
export const TIMELINE_PAGE = 20;
export const UPCOMING_FOLD = 3;

export const BOARD_RANGES = Object.freeze(['today', 'week', 'last7']);

export function describeNoticesError(e, fallback = NOTICES_GENERIC_ERROR) {
    return describeError(e, fallback);
}

// §1 — listPatientNotices({ pid, limit?, cursor? })
//   응답 { ok, pid, role, todayKey, dispatchHour, upcoming[], items[], nextCursor, total, truncated, errors[], texts{}, labels{} }
export async function listPatientNotices(pid, { limit, cursor } = {}) {
    const payload = { pid };
    if (Number.isInteger(limit) && limit >= 1) payload.limit = Math.min(limit, 200);
    if (typeof cursor === 'string' && cursor) payload.cursor = cursor;
    const res = await call('listPatientNotices')(payload);
    const d = res.data || {};
    return { ...d, upcoming: Array.isArray(d.upcoming) ? d.upcoming : [], items: Array.isArray(d.items) ? d.items : [], errors: Array.isArray(d.errors) ? d.errors : [], texts: d.texts || {}, labels: d.labels || {} };
}

// §2 — listClinicNoticeBoard({ range:'today'|'week'|'last7' })
//   응답 { ok, role, range, todayKey, generatedAt, dispatchHour, rows[], counts, truncated, errors[], previewNotice, texts{}, labels{}, clinicPhoneMissing }
export async function listClinicNoticeBoard(range) {
    if (!BOARD_RANGES.includes(range)) throw new Error(`range 형식 오류: ${range}`);
    const res = await call('listClinicNoticeBoard')({ range });
    const d = res.data || {};
    return normBoard(d);
}
const normBoard = (d) => ({ ...d, rows: Array.isArray(d.rows) ? d.rows : [], errors: Array.isArray(d.errors) ? d.errors : [], texts: d.texts || {}, labels: d.labels || {} });

// 10-15 로딩 개선: 여러 range 를 한 번에 — { ranges } → { range: 보드 | Error }. 서버가 옛 판(boards 없음)이면 null(호출부가 range 별로 다시 부른다)
export async function listClinicNoticeBoards(ranges) {
    const res = await call('listClinicNoticeBoard')({ ranges });
    const d = res.data || {};
    if (!d.boards || typeof d.boards !== 'object') return null;
    const out = {};
    for (const r of ranges) {
        const b = d.boards[r];
        if (b && b.ok !== false) out[r] = normBoard(b);
        else { const e = new Error('board range failed'); e.details = { code: (b && b.code) || 'internal' }; out[r] = e; }
    }
    return out;
}

// ── 표시 보조(순수 함수) ──
const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
const WEEKDAY = ['일', '월', '화', '수', '목', '금', '토'];

// 서버 generatedAt(ms) 을 KST 로 변환한 시(0~23). 브라우저 시계는 쓰지 않는다(계약 §5)
export function kstHourOf(ms) {
    if (!Number.isFinite(ms)) return null;
    return new Date(ms + KST_OFFSET_MS).getUTCHours();
}
export function kstDayKeyOf(ms) {
    if (!Number.isFinite(ms)) return '';
    return new Date(ms + KST_OFFSET_MS).toISOString().slice(0, 10);
}
// 「오늘」 구역 분기 — generatedAt 이 todayKey 당일 발송 시각 이후면 "아직 안 나간 것"
//   dispatchTime('HH:MM', 계약_알림파트_서버 §C-2 dispatchTimes[kind])이 있으면 분 단위로, 없으면 기존 dispatchHour(시) 기준
export function isAfterDispatch({ generatedAt, todayKey, dispatchHour, dispatchTime }) {
    const h = kstHourOf(generatedAt);
    if (h == null) return false;
    const day = kstDayKeyOf(generatedAt);
    if (todayKey && day && day > todayKey) return true;
    if (todayKey && day && day < todayKey) return false;
    const m = /^(\d{1,2}):(\d{2})$/.exec(String(dispatchTime || ''));
    if (m) {
        const nowMin = h * 60 + new Date(generatedAt + KST_OFFSET_MS).getUTCMinutes();
        return nowMin >= Number(m[1]) * 60 + Number(m[2]);
    }
    return h >= (Number.isFinite(dispatchHour) ? dispatchHour : 10);
}
// 보드 응답 dispatchTimes{roadmap,feedback,recall,clinic_notice:'HH:MM'} 에서 종류별 시각(없으면 '' → 기존 dispatchHour 동작)
export function dispatchTimeOf(d, kind) {
    const t = d?.dispatchTimes;
    return t && typeof t === 'object' && typeof t[kind] === 'string' ? t[kind] : '';
}
// 가장 늦은 종류의 발송 시각(머리 문구 전환용 — 전부 나간 뒤에 「나간 뒤」 문구). 없으면 ''
export function lastDispatchTimeOf(d) {
    const t = d?.dispatchTimes;
    if (!t || typeof t !== 'object') return '';
    return Object.values(t).filter(v => typeof v === 'string' && /^\d{1,2}:\d{2}$/.test(v))
        .sort((a, b) => a.padStart(5, '0').localeCompare(b.padStart(5, '0'))).pop() || '';
}

// 'YYYY-MM-DD' → 'MM.DD(요일)'
export function fmtDayKey(key) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || ''));
    if (!m) return String(key || '');
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    return `${m[2]}.${m[3]}(${WEEKDAY[d.getUTCDay()]})`;
}
// ms → 'YYYY.MM.DD HH:MM' (withTime) / 'YYYY.MM.DD' / 'MM.DD' (short)
export function fmtAt(ms, mode = 'full') {
    if (!Number.isFinite(ms)) return '';
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return '';
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    if (mode === 'short') return `${mm}.${dd}`;
    const base = `${d.getFullYear()}.${mm}.${dd}`;
    if (mode === 'date') return base;
    return `${base} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
// 'MM/DD HH:MM 방문 예정' 등은 서버 text 가 이미 만들어 보낸다 — 웹은 재구성하지 않는다

// 사건 행(문구 칸 비움) 판정 — 발송 행(sent·resent·skipped)만 문구를 가진다(계약 §1 items.text)
export const isSendEvent = (ev) => ev === 'sent' || ev === 'resent' || ev === 'skipped';
